import { z } from 'zod';

import { isoDateSchema, positiveIntSchema } from './common';
import { phaseIdSchema } from './ids';
import { matchRulesSettingsSchema } from './matchRules';

/**
 * Identifies a tournament format implementation, e.g. `group-stage`.
 *
 * Deliberately a slug rather than a fixed enum: the registry in
 * `core/engine` is the authority on which formats exist, so adding
 * Doppel-KO or Schweizer System later does not force an edit here.
 */
export const formatIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'Nur Kleinbuchstaben, Ziffern und Bindestriche')
  .brand<'FormatId'>();

export type FormatId = z.infer<typeof formatIdSchema>;

/**
 * One phase of the tournament. `config` is intentionally opaque here — each
 * format validates its own configuration with its own schema, which is what
 * keeps this file from growing a branch per format.
 */
export const phaseConfigSchema = z.object({
  id: phaseIdSchema,
  label: z.string().trim().min(1).max(64),
  formatId: formatIdSchema,
  config: z.unknown(),
});

export type PhaseConfig = z.infer<typeof phaseConfigSchema>;

export const generalSettingsSchema = z.object({
  name: z.string().trim().min(3, 'Bitte einen Turniernamen angeben').max(120),
  date: isoDateSchema,
  boardCount: positiveIntSchema.max(99),
  maxTeams: positiveIntSchema.max(512),
});

export type GeneralSettings = z.infer<typeof generalSettingsSchema>;

export const registrationSettingsSchema = z.object({
  isOpen: z.boolean(),
  /** Upper bound for the walk-on snippet. The "30 seconds" live here. */
  songMaxDurationSeconds: positiveIntSchema.max(600),
  allowMultipleTeamsPerFireDepartment: z.boolean(),
});

export type RegistrationSettings = z.infer<typeof registrationSettingsSchema>;

/**
 * How a single leg of X01 is scored. Configurable per tournament, as requested:
 * the counter reads the starting score and the double-out rule from here, never
 * from a constant in the code.
 */
export const gameSettingsSchema = z.object({
  /** Points every leg starts from — 301, 501 or 701. */
  startScore: z.union([z.literal(301), z.literal(501), z.literal(701)]),
  /** Whether the winning dart must land on a double (or the bullseye). */
  doubleOut: z.boolean(),
});

export type GameSettings = z.infer<typeof gameSettingsSchema>;

/**
 * Everything the tournament director can configure before the start.
 * Every rule the engine applies is read from here — there is no second place
 * where a tournament rule could hide.
 */
export const tournamentSettingsSchema = z
  .object({
    general: generalSettingsSchema,
    registration: registrationSettingsSchema,
    game: gameSettingsSchema,
    phases: z.array(phaseConfigSchema).min(1, 'Mindestens eine Phase wird benötigt').max(8),
    matchRules: matchRulesSettingsSchema,
  })
  .superRefine((settings, ctx) => {
    const ids = settings.phases.map((phase) => phase.id);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'Phasen-IDs müssen eindeutig sein',
        path: ['phases'],
      });
    }
  });

export type TournamentSettings = z.infer<typeof tournamentSettingsSchema>;
